import { useEffect, useState } from 'react';
import {
  COMPANY_ROLES,
  COMPANY_ROLE_HINT,
  COMPANY_ROLE_LABEL,
  manages as rolesManage,
  topRole,
  type CompanyMemberRow,
  type CompanyOverview,
  type CompanyRole,
  type MyCompanyRow,
} from 'shared';
import Crew from './Crew';
import Fleet from './Fleet';
import { useFormErrors } from '../useFormErrors';
import FieldError from '../FieldError';
import { api } from '../api';
import { alert, copyText } from '../telegram';
import { toast } from '../toast';

const money = (v: number) => `${v.toLocaleString('ru-RU')} ₽`;

/**
 * Компания как рабочее пространство.
 *
 * Диспетчер видит сотрудников, раздаёт права и смотрит сводку по всем
 * рейсам. Водитель сюда попадает только чтобы ввести код приглашения.
 */
export default function Company() {
  const [companies, setCompanies] = useState<MyCompanyRow[]>([]);
  const [active, setActive] = useState<MyCompanyRow | null>(null);
  const [members, setMembers] = useState<CompanyMemberRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [code, setCode] = useState('');
  const [invite, setInvite] = useState<{ code: string; role: string } | null>(null);
  const [inviteRole, setInviteRole] = useState<CompanyRole>('DRIVER');
  const [inviteRates, setInviteRates] = useState(false);
  const [inviteBook, setInviteBook] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * Вкладки внутри компании.
   *
   * «Экипаж» — то, ради чего сюда заходят каждый день; «права» и «сводка» —
   * раз в месяц. Держать их одним свитком значит каждый раз пролистывать
   * галочки, чтобы узнать, где машина.
   */
  const [tab, setTab] = useState<'crew' | 'fleet' | 'rights'>('crew');
  /** Сводка над вкладками: чем контора располагает и что в работе. */
  const [overview, setOverview] = useState<CompanyOverview | null>(null);
  const joinForm = useFormErrors();

  const manages = active ? rolesManage(active.roles) : false;
  const owner = Boolean(active?.roles.includes('OWNER'));

  /**
   * Компания из профиля — та, чей ИНН человек вписал себе сам.
   *
   * Это НЕ то же самое, что место в компании: привязка ИНН ставит
   * `User.companyId` и только, а роль живёт в месте. Пока эти два состояния
   * не различались на экране, человек считал себя в компании и не понимал,
   * почему у него нет ни прав, ни сотрудников.
   */
  const [profileCompany, setProfileCompany] = useState<{
    id: string;
    name: string;
    inn: string;
  } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [r, me] = await Promise.all([api.myCompanies(), api.me()]);
      setCompanies(r.items);
      setActive((prev) => r.items.find((c) => c.id === prev?.id) ?? r.items[0] ?? null);
      setProfileCompany(me.company);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!active) return;
    // Состав теперь отдаётся любому работнику компании — без контактов,
    // если он ею не управляет. «Кто владелец и кто диспетчер» водитель
    // до сих пор выяснял по телефону.
    void api
      .companyMembers(active.id)
      .then((r) => setMembers(r.items))
      .catch(() => setMembers([]));

    if (!rolesManage(active.roles)) {
      setOverview(null);
      return;
    }
    void api
      .companyOverview(active.id)
      .then(setOverview)
      .catch(() => {});
  }, [active]);

  async function join() {
    if (!code.trim()) return joinForm.fail({ code: 'Введите код приглашения' });

    joinForm.reset();
    setBusy(true);
    try {
      const r = await api.joinCompany(code.trim());
      setCode('');
      toast.success(`Вы в компании «${r.name}»`);
      await load();
    } catch (e) {
      // Отказ относится к введённому коду — показываем под ним, а не в окне.
      joinForm.fail({ code: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function claim() {
    setBusy(true);
    try {
      await api.claimCompany();
      await load();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function makeInvite() {
    if (!active) return;
    setBusy(true);
    try {
      const r = await api.createInvite(active.id, {
        role: inviteRole,
        canSeeRates: inviteRates,
        canBook: inviteBook,
      });
      setInvite({ code: r.code, role: r.role });
      toast.success('Код готов — действует сутки');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function patch(userId: string, data: Record<string, unknown>) {
    if (!active) return;
    try {
      await api.patchMember(active.id, userId, data);
      const r = await api.companyMembers(active.id);
      setMembers(r.items);
      toast.success('Права сохранены');
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  if (loading) return <div className="card">Загружаю…</div>;

  return (
    <>
      {companies.length === 0 && (
        <div className="card">
          <h3>{profileCompany ? 'Вы не оформлены в компании' : 'Компании нет'}</h3>

          {/* Самое непонятное состояние во всей системе, и до сих пор оно
              никак не называлось: ИНН в профиле стоит, компания в базе
              есть, а места в ней нет — значит, нет и роли. Человек считал
              себя в компании и не понимал, почему у него ничего нет. */}
          {profileCompany ? (
            <div className="meta" style={{ marginBottom: 12 }}>
              В профиле у вас указан ИНН {profileCompany.inn} — компания «
              {profileCompany.name}». Но места в ней у вас нет, поэтому нет и
              роли. Если компания ваша — нажмите «Я владелец». Если вы в ней
              работаете — попросите у владельца код приглашения: роль и права
              выдаёт он.
            </div>
          ) : (
            <div className="meta" style={{ marginBottom: 12 }}>
              Если работаете на компанию — введите код, который выдал диспетчер.
              Если компания ваша — привяжите ИНН в профиле и станьте владельцем.
            </div>
          )}

          <div className="field">
            <label>Код приглашения</label>
            <input
              {...joinForm.field('code')}
              value={code}
              placeholder="AB12CD"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <FieldError form={joinForm} name="code" />
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button disabled={busy} onClick={() => void join()}>
              Войти по коду
            </button>
            <button className="secondary" disabled={busy} onClick={() => void claim()}>
              Я владелец
            </button>
          </div>
        </div>
      )}

      {companies.length > 1 && (
        <div className="card">
          <h3>Компания</h3>
          <div className="chips">
            {companies.map((c) => (
              <button
                key={c.id}
                className={c.id === active?.id ? 'chip chip--on' : 'chip'}
                onClick={() => setActive(c)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {active && (
        <div className="card">
          <h3>{active.name}</h3>
          <div className="meta">
            {/* Компания, заведённая по ИНН без названия, называется этим же
                ИНН — и он печатался дважды подряд. Повторяем только тогда,
                когда название о нём молчит. */}
            {active.name.includes(active.inn) ? '' : `ИНН ${active.inn} · `}
            вы — {active.roles.map((r) => COMPANY_ROLE_LABEL[r].toLowerCase()).join(', ')}
            {active.verified ? ' · проверена' : ''}
          </div>
          {!manages && (
            <div className="meta" style={{ marginTop: 8 }}>
              {COMPANY_ROLE_HINT[topRole(active.roles)]}
            </div>
          )}
        </div>
      )}

      {/*
        * Сводка над вкладками.
        *
        * Компания — это машины, прицепы и люди вместе, и первый взгляд нужен
        * общий: есть ли на что брать заказ и чем всё это кончается по деньгам.
        * Подробности — на вкладках, но лезть в них ради «сколько машин
        * свободно» человек не должен.
        */}
      {/* Кто здесь кто — всем работникам, а не только управляющим.
          Управляющие видят тот же состав ниже, вместе с правами и
          контактами; наёмному водителю нужно ровно это: к кому идти. */}
      {active && !manages && members.length > 0 && (
        <div className="card">
          <h3>Кто есть кто</h3>
          {members
            .filter((m) => m.active)
            .map((m) => (
              <div key={m.userId} className="row" style={{ marginBottom: 'var(--s2)' }}>
                <span>{m.name}</span>
                <span className="meta">
                  {m.roles.map((r) => COMPANY_ROLE_LABEL[r]).join(', ')}
                </span>
              </div>
            ))}
          {!members.some((m) => m.active && m.roles.includes('OWNER')) && (
            <div className="meta" style={{ marginTop: 8 }}>
              Владелец у компании не назначен — права выдать некому. Напишите
              в поддержку, если это ваша компания.
            </div>
          )}
        </div>
      )}

      {active && manages && overview && (
        <div className="card">
          <div className="kpis">
            <div className="kpi">
              <span className="kpi__label">Машины</span>
              <strong className="kpi__value">
                {overview.fleet.free} / {overview.fleet.total}
              </strong>
              <span className="meta">
                свободно
                {overview.fleet.trailers > 0
                  ? ` · прицепов ${overview.fleet.trailers}`
                  : ''}
              </span>
            </div>
            <div className="kpi">
              <span className="kpi__label">Люди</span>
              <strong className="kpi__value">{overview.people.total}</strong>
              <span className="meta">
                водителей {overview.people.drivers}
              </span>
            </div>
            <div className="kpi">
              <span className="kpi__label">Рейсы</span>
              <strong className="kpi__value">{overview.orders.active}</strong>
              <span className="meta">
                готово {overview.orders.done} · отменено{' '}
                {overview.orders.cancelled}
              </span>
            </div>
            <div className="kpi">
              <span className="kpi__label">Чистыми</span>
              <strong
                className={`kpi__value ${overview.money.netRub < 0 ? 'neg' : 'pos'}`}
              >
                {money(overview.money.netRub)}
              </strong>
              <span className="meta">за всё время</span>
            </div>
          </div>
        </div>
      )}

      {active && manages && (
        <div className="seg" style={{ marginBottom: 12 }}>
          <button
            className={tab === 'crew' ? 'seg__btn seg__btn--on' : 'seg__btn'}
            onClick={() => setTab('crew')}
          >
            Экипаж
          </button>
          <button
            className={tab === 'fleet' ? 'seg__btn seg__btn--on' : 'seg__btn'}
            onClick={() => setTab('fleet')}
          >
            Парк
          </button>
          <button
            className={tab === 'rights' ? 'seg__btn seg__btn--on' : 'seg__btn'}
            onClick={() => setTab('rights')}
          >
            Управление персоналом
          </button>
        </div>
      )}

      {active && manages && tab === 'crew' && (
        <Crew
          companyId={active.id}
          // Нанимает владелец. Логист распоряжается рейсами, но заводить
          // учётные записи и раздавать пароли — не его дело.
          canHire={owner}
        />
      )}

      {active && manages && tab === 'fleet' && (
        <Fleet companyId={active.id} canManage={manages} />
      )}

      {active && manages && tab === 'rights' && (
        <div className="card">
          <h3>Сотрудники</h3>

          {members.map((m) => (
            <div key={m.userId} className="member">
              <div>
                <strong>{m.name}</strong>
                <div className="meta">
                  {m.roles.map((r) => COMPANY_ROLE_LABEL[r]).join(', ')}
                  {m.ordersActive > 0 ? ` · в рейсе: ${m.ordersActive}` : ''}
                  {!m.active ? ' · уволен' : ''}
                </div>

                {/* Всё, что о человеке известно, — в одном месте: искать
                    телефон водителя по трём экранам диспетчер не станет. */}
                <div className="meta">
                  {m.phone ? <a href={`tel:${m.phone}`}>{m.phone}</a> : 'телефон не указан'}
                  {m.email ? ` · ${m.email}` : ''}
                  {m.tgUsername ? ` · @${m.tgUsername}` : ''}
                  {m.login ? ` · логин ${m.login}` : ''}
                </div>
                <div className="meta">
                  Документов: {m.docsCount}
                  {' · в компании с '}
                  {new Date(m.createdAt).toLocaleDateString('ru-RU')}
                </div>
              </div>

              {!m.roles.includes('OWNER') && (
                <div className="member-controls">
                  {/* Роли набором: владелец малой конторы сам возит и сам
                      договаривается, и выбирать «кем он числится» не нужно. */}
                  {COMPANY_ROLES.filter((r) => r !== 'OWNER' || owner).map((r) => (
                    <label key={r} className="check">
                      <input
                        type="checkbox"
                        checked={m.roles.includes(r)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...m.roles, r]
                            : m.roles.filter((x) => x !== r);
                          if (next.length === 0) {
                            toast.error('Оставьте хотя бы одну роль');
                            return;
                          }
                          void patch(m.userId, { roles: next });
                        }}
                      />
                      {COMPANY_ROLE_LABEL[r]}
                    </label>
                  ))}

                  {/* Без этой галочки человека нет в подборе экипажа:
                      диспетчер, который иногда ездит сам, — обычное дело,
                      но по умолчанию за руль его не ставят. */}
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={m.canDrive}
                      onChange={(e) =>
                        void patch(m.userId, { canDrive: e.target.checked })
                      }
                    />
                    прикреплять к авто
                  </label>

                  <label className="check">
                    <input
                      type="checkbox"
                      checked={m.canSeeRates}
                      onChange={(e) =>
                        void patch(m.userId, { canSeeRates: e.target.checked })
                      }
                    />
                    видит ставки
                  </label>

                  <label className="check">
                    <input
                      type="checkbox"
                      checked={m.canBook}
                      onChange={(e) => void patch(m.userId, { canBook: e.target.checked })}
                    />
                    берёт грузы
                  </label>

                  <button
                    className="secondary btn--compact"
                    onClick={() => void patch(m.userId, { active: !m.active })}
                  >
                    {m.active ? 'Уволить' : 'Вернуть в компанию'}
                  </button>
                </div>
              )}
            </div>
          ))}

          <div className="divider" />

          <h3>Пригласить</h3>
          <div className="field-row">
            <div className="field">
              <label>Роль</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as CompanyRole)}
              >
                {COMPANY_ROLES.filter((r) => r !== 'OWNER').map((r) => (
                  <option key={r} value={r}>
                    {COMPANY_ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="check">
            <input
              type="checkbox"
              checked={inviteRates}
              onChange={(e) => setInviteRates(e.target.checked)}
            />
            видит суммы сделок
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={inviteBook}
              onChange={(e) => setInviteBook(e.target.checked)}
            />
            может сам брать грузы
          </label>

          <button className="secondary" disabled={busy} onClick={() => void makeInvite()}>
            Получить код
          </button>

          {invite && (
            <div
              className="invite"
              role="button"
              tabIndex={0}
              onClick={() => {
                void copyText(invite.code);
                toast.success('Код скопирован');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void copyText(invite.code);
                  toast.success('Код скопирован');
                }
              }}
            >
              <span>{invite.code}</span>
              <span className="meta">нажмите, чтобы скопировать · действует сутки</span>
            </div>
          )}
        </div>
      )}

    </>
  );
}
